import type { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { siDiscord, siFacebook } from 'simple-icons';
import { track } from '../../utils/track';
import { useOfferSlug } from './giveawayOfferPages';
import { CYAN, INK, BODY, LINE } from './giveawayOfferTheme';

/* Facebook and Discord, under the headline of the confirmation and subscribed
 * pages, and named inline in the confirmation page's steps.
 *
 * ONE JOB EACH, and the copy must keep them apart: FACEBOOK IS WHERE THE LIVE
 * DRAW IS WATCHED; DISCORD IS WHERE THE COMMUNITY IS. Nothing in this funnel may
 * say or imply the draw is watched in Discord.
 *
 * THE URLS LIVE HERE, ONCE — both are the footer's (`RaffleFooter`). The
 * subscribed page used to carry a Discord invite of its own; every invite code
 * in this repo leads to the same server, but one place to change it is the
 * point, so that page imports `DISCORD_URL` from here.
 *
 * The logos are drawn the way RaffleFooter draws them (simple-icons path in an
 * inline svg), in the brand colours rather than the footer's monochrome. Every
 * click is tracked with the network and the page it came from.
 */

export const FACEBOOK_URL = 'https://www.facebook.com/incryptcrypto/';
export const DISCORD_URL = 'https://discord.gg/W8FGSQgW3S';

type Network = 'facebook' | 'discord';
/** The page a link sits on, for the click event: this funnel's two, and the
 * promo pack's two (`features/PromoPack`), which share the row and its copy. */
type OnPage =
  | 'confirmation'
  | 'subscribed'
  | 'promo-pack'
  | 'promo-pack-confirmation';

const NETWORKS = [
  {
    network: 'facebook' as const,
    href: FACEBOOK_URL,
    path: siFacebook.path,
    colour: '#1877F2',
    title: 'Follow us on Facebook',
    line: 'Watch the draws live'
  },
  {
    network: 'discord' as const,
    href: DISCORD_URL,
    path: siDiscord.path,
    colour: '#5865F2',
    title: 'Join our Discord',
    line: 'Get involved in the community'
  }
];

/* The promo pack's pages sit on no offer path, so `useOfferSlug` would answer
 * `giveaway-offer` for them; their clicks carry no `offerSlug` rather than that. */
const useTrackSocialClick = (page: OnPage, placement: 'row' | 'steps') => {
  const offerSlug = useOfferSlug();
  const onOfferPath = page === 'confirmation' || page === 'subscribed';
  return (network: Network) =>
    track('giveaway_offer.social_click', {
      network,
      page,
      placement,
      ...(onOfferPath ? { offerSlug } : {})
    });
};

/** The two links as a row: stacked on a phone, side by side from `sm`. Each is
 * a real anchor at least 44px tall, opening in a new tab. */
const GiveawayOfferSocialLinks = ({ page }: { page: OnPage }) => {
  const trackClick = useTrackSocialClick(page, 'row');

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          sm: 'repeat(2, minmax(0, 1fr))'
        },
        gap: 1.5,
        maxWidth: 560,
        mx: 'auto'
      }}
    >
      {NETWORKS.map((n) => (
        <Box
          key={n.network}
          component="a"
          href={n.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackClick(n.network)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            minHeight: 56,
            px: 2,
            py: 1.25,
            borderRadius: '14px',
            border: `1px solid ${LINE}`,
            background: 'rgba(255,255,255,0.03)',
            textAlign: 'left',
            textDecoration: 'none',
            transition: 'border-color 160ms ease, background 160ms ease',
            '&:hover': {
              borderColor: CYAN,
              background: 'rgba(56,232,255,0.06)'
            },
            '&:focus-visible': { outline: `2px solid ${CYAN}`, outlineOffset: 2 }
          }}
        >
          <Box
            component="svg"
            viewBox="0 0 24 24"
            aria-hidden="true"
            sx={{ width: 28, height: 28, flexShrink: 0, fill: n.colour }}
          >
            <path d={n.path} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="span"
              sx={{
                display: 'block',
                fontSize: 15,
                fontWeight: 800,
                color: INK,
                lineHeight: 1.3
              }}
            >
              {n.title}
            </Typography>
            <Typography
              component="span"
              sx={{ display: 'block', fontSize: 13.5, color: BODY, lineHeight: 1.4 }}
            >
              {n.line}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

/** One network named inside running text — the steps' "on Facebook". The
 * funnel's cyan, underlined so it reads as a link without relying on colour. */
export const SocialTextLink = ({
  network,
  page,
  children
}: {
  network: Network;
  page: OnPage;
  children: ReactNode;
}) => {
  const trackClick = useTrackSocialClick(page, 'steps');

  return (
    <Box
      component="a"
      href={network === 'facebook' ? FACEBOOK_URL : DISCORD_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackClick(network)}
      sx={{
        color: CYAN,
        fontWeight: 700,
        textDecoration: 'underline',
        textDecorationColor: 'rgba(56,232,255,0.45)',
        textUnderlineOffset: '3px',
        '&:hover': { textDecorationColor: CYAN },
        '&:focus-visible': {
          outline: `2px solid ${CYAN}`,
          outlineOffset: 2,
          borderRadius: '2px'
        }
      }}
    >
      {children}
    </Box>
  );
};

export default GiveawayOfferSocialLinks;
